import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { findOptionsForSelectFilter } from '../../util/search';
import { propTypes } from '../../util/types';
import { parse } from '../../util/urlHelpers';
import { ensureCurrentUser } from '../../util/data';
import { prepareSocialSharingImages } from '../../util/seo';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { addPostCount } from './CommunityPage.duck';
import {
  Page,
  NamedRedirect,
  LayoutSingleColumn,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
} from '../../components';
import { TopbarContainer } from '..';
import config from '../../config';

// Shared components
import SectionFeatured from './SectionFeatured';
import Sidebar from './Sidebar/Sidebar';
import Posts from './Posts/Posts';

import communityPageSharingImage from '../../assets/attractrCommunity-1200x630.avif';
import css from './CommunityPage.module.css';

/**
 * Main content area of the community page
 */
const CommunityContent = ({
  categories,
  disciplines,
  roles,
  topics,
  currentSearchParams,
  currentUser,
  featuredPosts,
  fetchFeaturedPostsInProgress,
  fetchFeaturedPostsError,
  history,
  intl,
  linkPreviews,
  location,
  onAddPostCount,
  pagePosts,
  pagination,
  addPostCountInProgress,
  addPostCountError,
  searchPostsInProgress,
  searchPostsError,
  sidebarTeam,
  sidebarMemberCount,
  onManageDisableScrolling,
}) => (
  <div className={css.pageWrapper}>
    {/* Featured posts section */}
    <SectionFeatured
      featuredPosts={featuredPosts}
      fetchFeaturedPostsInProgress={fetchFeaturedPostsInProgress}
      fetchFeaturedPostsError={fetchFeaturedPostsError}
      currentSearchParams={currentSearchParams}
    />

    {/* Main content and sidebar */}
    <div className={css.contentWrapper}>
      <Posts
        intl={intl}
        location={location}
        history={history}
        currentUser={currentUser}
        categories={categories}
        topics={topics}
        disciplines={disciplines}
        roles={roles}
        posts={pagePosts}
        currentSearchParams={currentSearchParams}
        pagination={pagination}
        searchParamsForPagination={currentSearchParams}
        searchPostsInProgress={searchPostsInProgress}
        searchPostsError={searchPostsError}
        addPostCountInProgress={addPostCountInProgress}
        addPostCountError={addPostCountError}
        onAddPostCount={onAddPostCount}
        linkPreviews={linkPreviews}
      />
      <Sidebar
        history={history}
        categories={categories}
        disciplines={disciplines}
        roles={roles}
        topics={topics}
        currentSearchParams={currentSearchParams}
        team={sidebarTeam}
        memberCount={sidebarMemberCount}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </div>
  </div>
);

/**
 * Community page component
 * Displays featured posts, post listing, and sidebar categories
 */
const CommunityPageComponent = props => {
  const {
    scrollingDisabled,
    currentUser,
    intl,
    history,
    location,
    pagePosts,
    featuredPosts,
    searchPostsInProgress,
    searchPostsError,
    fetchFeaturedPostsInProgress,
    fetchFeaturedPostsError,
    addPostCountInProgress,
    addPostCountError,
    onAddPostCount,
    linkPreviews,
    pagination,
    filterConfig,
    sidebarTeam,
    sidebarMemberCount,
    onManageDisableScrolling,
  } = props;

  // Get filter options (categories + discipline + role + topics) from filter config.
  const categories = findOptionsForSelectFilter('community-categories', filterConfig);
  const disciplines = findOptionsForSelectFilter('community-disciplines', filterConfig);
  const roles = findOptionsForSelectFilter('community-roles', filterConfig);
  const topics = findOptionsForSelectFilter('community-topics', filterConfig);

  // Parse current search parameters from URL
  const currentSearchParams = parse(location.search);

  // Ensure current user data is available
  const ensuredCurrentUser = ensureCurrentUser(currentUser);

  // Belt-and-braces guard: brand-new community members who land on
  // /community directly (refresh, shared link, OAuth callback that
  // doesn't carry a `from`) still get pushed through the one-shot
  // profile prompt. Only applies once they're logged in.
  const promptDone = !!ensuredCurrentUser?.attributes?.profile?.privateData
    ?.communityProfilePromptDone;
  if (ensuredCurrentUser?.id && !promptDone) {
    return <NamedRedirect name="CommunityProfilePromptPage" />;
  }

  // Format page title for SEO
  const schemaTitle = intl.formatMessage(
    { id: 'CommunityPage.schemaTitle' },
    { siteTitle: config.siteTitle }
  );
  const schemaDescription = intl.formatMessage({ id: 'CommunityPage.schemaDescription' });
  const schemaKeywords = intl.formatMessage({ id: 'CommunityPage.schemaKeywords' });

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
      keywords={schemaKeywords}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'CommunityPage',
        name: schemaTitle,
      }}
      socialSharing={{
        images1200: prepareSocialSharingImages([communityPageSharingImage]),
      }}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="CommunityPage" />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain>
          <CommunityContent
            categories={categories}
            disciplines={disciplines}
            roles={roles}
            topics={topics}
            currentSearchParams={currentSearchParams}
            currentUser={ensuredCurrentUser}
            featuredPosts={featuredPosts}
            fetchFeaturedPostsInProgress={fetchFeaturedPostsInProgress}
            fetchFeaturedPostsError={fetchFeaturedPostsError}
            history={history}
            intl={intl}
            linkPreviews={linkPreviews}
            location={location}
            onAddPostCount={onAddPostCount}
            pagePosts={pagePosts}
            pagination={pagination}
            addPostCountInProgress={addPostCountInProgress}
            addPostCountError={addPostCountError}
            searchPostsInProgress={searchPostsInProgress}
            searchPostsError={searchPostsError}
            sidebarTeam={sidebarTeam}
            sidebarMemberCount={sidebarMemberCount}
            onManageDisableScrolling={onManageDisableScrolling}
          />
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

CommunityPageComponent.defaultProps = {
  filterConfig: config.custom.filters,
  searchPostsInProgress: false,
  searchPostsError: null,
  fetchFeaturedPostsInProgress: false,
  fetchFeaturedPostsError: null,
  addPostCountInProgress: false,
  addPostCountError: null,
};

const { arrayOf, bool, func, shape, string } = PropTypes;

CommunityPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  filterConfig: propTypes.filterConfig,
  sortConfig: propTypes.sortConfig,
  searchPostsInProgress: bool.isRequired,
  searchPostsError: propTypes.error,
  fetchFeaturedPostsInProgress: bool.isRequired,
  fetchFeaturedPostsError: propTypes.error,
  addPostCountInProgress: bool.isRequired,
  addPostCountError: propTypes.error,
  linkPreviews: arrayOf(
    shape({
      id: propTypes.uuid,
      linkPreview: shape({
        title: string,
        description: string,
        image: string,
        url: string,
      }),
    })
  ),

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

/**
 * Maps Redux state to component props
 */
const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    searchPostsInProgress,
    searchPostsError,
    featuredPostsResultIds,
    fetchFeaturedPostsInProgress,
    fetchFeaturedPostsError,
    linkPreviews,
    pagination,
    addPostCountInProgress,
    addPostCountError,
    sidebarTeam,
    sidebarMemberCount,
  } = state.CommunityPage;

  // Get full post objects from IDs
  const pagePosts = getListingsById(state, currentPageResultIds);
  const featuredPosts = getListingsById(state, featuredPostsResultIds);

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    pagePosts,
    searchPostsInProgress,
    searchPostsError,
    featuredPosts,
    fetchFeaturedPostsInProgress,
    fetchFeaturedPostsError,
    linkPreviews,
    pagination,
    addPostCountInProgress,
    addPostCountError,
    sidebarTeam,
    sidebarMemberCount,
  };
};

/**
 * Maps Redux actions to component props
 */
const mapDispatchToProps = dispatch => ({
  onAddPostCount: params => dispatch(addPostCount(params)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Connect component to Redux and add router/intl functionality
const CommunityPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
  injectIntl
)(CommunityPageComponent);

export default CommunityPage;
